import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, useApi, useAuth } from '../../hooks';

/**
 * Fetches a Healthie document's file content by id and exposes it as an
 * object URL for an iframe/img `src`. Healthie's `expiring_url` is only
 * valid for ~10 seconds, so the backend fetches and streams it fresh on
 * every call -- this hook should not be treated as returning a stable,
 * cacheable link.
 */
export const useFetchDocument = (userId: string, documentId: number | null) => {
  const { api } = useApi({ throwIfError: true });
  const { isAuthenticated, loading, userToken } = useAuth();

  const fetchDocument = async () => {
    const requestObj = {
      route: `/private/dashboard/user/${userId}/document/${documentId}`,
      token: userToken,
      responseType: 'blob' as const,
    };

    return api(requestObj);
  };

  const { data: blob, isInitialLoading, isLoading, error } = useQuery<
    Blob,
    ApiError
  >(['fetch-document', userToken, userId, documentId], fetchDocument, {
    enabled:
      isAuthenticated &&
      !loading &&
      !!userToken &&
      !!userId &&
      documentId != null,
    refetchOnWindowFocus: false,
    // A 404 means the document doesn't exist - retrying won't change that,
    // so fail immediately instead of retrying like a transient error.
    retry: (failureCount, error) => error.status !== 404 && failureCount < 3,
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      return;
    }

    const url = URL.createObjectURL(blob);
    setObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  return {
    objectUrl,
    isInitialLoading,
    isLoading,
    error,
  };
};
